/**
 * Validation State Slice
 *
 * Manages validation results, errors, warnings, and optimization suggestions.
 */

import type { StateCreator } from 'zustand';
import type { ValidationResult, ValidationMessage } from '../types/validation';
import type { ValidationReport } from '../validation/ValidationOrchestrator';

export interface ValidationSlice {
  // State
  validationResult: ValidationResult | null;
  validationReport: ValidationReport | null;
  isValidating: boolean;
  lastValidated: Date | null;

  // Optimization suggestions
  optimizationSuggestions: string[];

  // Actions
  setValidationResult: (result: ValidationResult) => void;
  setValidationReport: (report: ValidationReport) => void;
  setIsValidating: (isValidating: boolean) => void;
  clearValidation: () => void;

  // Optimization
  setOptimizationSuggestions: (suggestions: string[]) => void;
  addOptimizationSuggestion: (suggestion: string) => void;
  clearOptimizationSuggestions: () => void;

  // Helpers
  hasErrors: () => boolean;
  hasWarnings: () => boolean;
  getErrorCount: () => number;
  getWarningCount: () => number;
  getFieldErrors: (field: string) => ValidationMessage[];
  getFieldWarnings: (field: string) => ValidationMessage[];
}

export const createValidationSlice: StateCreator<
  ValidationSlice,
  [],
  [],
  ValidationSlice
> = (set, get) => ({
  // Initial state
  validationResult: null,
  validationReport: null,
  isValidating: false,
  lastValidated: null,
  optimizationSuggestions: [],

  // Actions
  setValidationResult: (result) =>
    set({
      validationResult: result,
      lastValidated: new Date(),
    }),

  setValidationReport: (report) =>
    set({
      validationReport: report,
      validationResult: report.overall,
      lastValidated: new Date(),
    }),

  setIsValidating: (isValidating) =>
    set({ isValidating }),

  clearValidation: () =>
    set({
      validationResult: null,
      validationReport: null,
      lastValidated: null,
    }),

  // Optimization
  setOptimizationSuggestions: (suggestions) =>
    set({ optimizationSuggestions: suggestions }),

  addOptimizationSuggestion: (suggestion) =>
    set((state) => ({
      optimizationSuggestions: [...state.optimizationSuggestions, suggestion],
    })),

  clearOptimizationSuggestions: () =>
    set({ optimizationSuggestions: [] }),

  // Helpers
  hasErrors: () => {
    const result = get().validationResult;
    return result ? result.errorCount > 0 : false;
  },

  hasWarnings: () => {
    const result = get().validationResult;
    return result ? result.warningCount > 0 : false;
  },

  getErrorCount: () => {
    const result = get().validationResult;
    return result ? result.errorCount : 0;
  },

  getWarningCount: () => {
    const result = get().validationResult;
    return result ? result.warningCount : 0;
  },

  getFieldErrors: (field) => {
    const result = get().validationResult;
    if (!result || !result.fields[field]) {
      return [];
    }
    return result.fields[field].messages.filter((m) => m.severity === 'error');
  },

  getFieldWarnings: (field) => {
    const result = get().validationResult;
    if (!result || !result.fields[field]) {
      return [];
    }
    return result.fields[field].messages.filter((m) => m.severity === 'warning');
  },
});
